import { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import {
    focusElement,
    getFocusableElems,
    trapFocus,
} from "tsi-common-react/src/utils/keyboardFocus";
import { loadWistiaPlayer } from "tsi-common-react/src/utils/wistia";

import type { VideoBarBlock as VideoBarBlockValue } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type WistiaVideo = {
    bind: (eventName: string, callback: () => void) => void;
    time: (seconds: number) => void;
    play: () => void;
    popover: {
        _popoverContainer: HTMLElement;
        hide: () => void;
        show: () => void;
    };
};

type WistiaWindow = Window & {
    _wq?: Array<{ xsq4: (video: WistiaVideo) => void }>;
    Wistia?: {
        embeds?: {
            setup?: () => void;
        };
    };
};

const WISTIA_EMBED_CLASS_NAME =
    "wistia_embed wistia_async_xsq4yntzgn popover=True";

let requestedWistiaStart: number | null = null;
let wistiaVideo: WistiaVideo | null = null;
let wistiaRegistered = false;
let removeWistiaTrapFocus: (() => void) | null = null;
const boundWistiaVideos = new WeakSet<WistiaVideo>();

export type VideoBarAsset = Immutable<{
    start: string;
    image: string;
    alt: string;
    caption: string;
}>;

const getServerRenderedVideoImageSrc = (start: string): string | null => {
    const document = getWindow()?.document;
    if (!document) {
        return null;
    }

    const videoElems = document.querySelectorAll<HTMLElement>(
        "[data-tsi-react-video-bar] .mattress-video",
    );
    const videoElem = Array.from(videoElems).find(
        (elem) => elem.dataset.start === start,
    );

    return videoElem?.querySelector("img")?.getAttribute("src") ?? null;
};

const setupWistiaEmbeds = (): void => {
    const window = getWindow() as WistiaWindow | undefined;
    window?.Wistia?.embeds?.setup?.();
};

const loadAndSetupWistiaPlayer = async (): Promise<void> => {
    await loadWistiaPlayer();
    setupWistiaEmbeds();
};

const reportWistiaLoadFailure = (error: unknown): void => {
    const window = getWindow();
    window?.console.error("Unable to load Cocoon Wistia video.", error);
};

const cleanupWistiaTrapFocus = (): void => {
    removeWistiaTrapFocus?.();
    removeWistiaTrapFocus = null;
};

const clearRequestedWistiaStart = (): void => {
    requestedWistiaStart = null;
};

const consumeRequestedWistiaStart = (): number | null => {
    const start = requestedWistiaStart;
    clearRequestedWistiaStart();
    return start;
};

const showPendingWistiaVideo = (video: WistiaVideo): void => {
    if (requestedWistiaStart !== null) {
        video.popover.show();
    }
};

const bindWistiaVideo = (video: WistiaVideo): void => {
    wistiaVideo = video;
    if (boundWistiaVideos.has(video)) {
        showPendingWistiaVideo(video);
        return;
    }
    boundWistiaVideos.add(video);

    video.bind("play", () => {
        const start = consumeRequestedWistiaStart();
        if (start !== null) {
            video.time(start);
        }
    });
    video.bind("end", () => {
        clearRequestedWistiaStart();
        cleanupWistiaTrapFocus();
        video.popover.hide();
    });
    video.bind("popoverhide", () => {
        clearRequestedWistiaStart();
        cleanupWistiaTrapFocus();
    });
    video.bind("popovershow", () => {
        cleanupWistiaTrapFocus();
        const elems = getFocusableElems(video.popover._popoverContainer);
        if (elems.length > 0) {
            focusElement(elems[0]);
        }
        removeWistiaTrapFocus = trapFocus(video.popover._popoverContainer);
        video.play();
    });
    showPendingWistiaVideo(video);
};

const registerWistiaVideo = (): void => {
    const window = getWindow() as WistiaWindow | undefined;
    if (!window || wistiaRegistered) {
        return;
    }
    window._wq = window._wq || [];
    window._wq.push({
        xsq4: bindWistiaVideo,
    });
    wistiaRegistered = true;
};

const showWistiaVideo = (start: string): void => {
    requestedWistiaStart = Number(start);
    if (wistiaVideo) {
        wistiaVideo.popover.show();
        return;
    }
    registerWistiaVideo();
    void loadAndSetupWistiaPlayer().catch(reportWistiaLoadFailure);
};

export const useCocoonWistiaPopoverVideo = (): {
    embedRef: React.RefObject<HTMLDivElement | null>;
    showVideo: (start: string) => void;
} => {
    const embedRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        const embed = embedRef.current;
        if (!embed) {
            return;
        }
        embed.className = WISTIA_EMBED_CLASS_NAME;
        registerWistiaVideo();
        void loadAndSetupWistiaPlayer().catch(() => {});
        return cleanupWistiaTrapFocus;
    }, []);

    const showVideo = React.useCallback((start: string) => {
        showWistiaVideo(start);
    }, []);

    return { embedRef, showVideo };
};

export const CocoonWistiaPopoverEmbed = (props: {
    embedRef: React.RefObject<HTMLDivElement | null>;
}): React.JSX.Element => {
    return <div ref={props.embedRef} style={{ visibility: "hidden" }} />;
};

export const CocoonVideoBarBlock = (props: {
    value: Immutable<VideoBarBlockValue>;
    videos: Immutable<VideoBarAsset[]>;
}): React.JSX.Element => {
    const { embedRef, showVideo } = useCocoonWistiaPopoverVideo();

    return (
        <section
            className={concatClassNames([
                styles.videoBarBlock,
                getGlobalSpacingClasses(props.value.spacing),
            ])}
            // Scopes getServerRenderedVideoImageSrc's lookup to this block's own
            // server-rendered thumbnails.
            data-tsi-react-video-bar=""
        >
            {props.value.title ? (
                <h2 className={styles.videoBarBlockTitle}>
                    {props.value.title}
                </h2>
            ) : null}
            {props.value.subtitle ? (
                <h3
                    className={concatClassNames([
                        styles.videoBarBlockSubtitle,
                        "caption-light",
                    ])}
                >
                    {props.value.subtitle}
                </h3>
            ) : null}
            <CocoonWistiaPopoverEmbed embedRef={embedRef} />
            <div className="main-container">
                <article id="mattress-videos">
                    {props.videos.map((video) => (
                        <div
                            className={concatClassNames([
                                styles.videoBarBlockVideo,
                                "mattress-video",
                            ])}
                            data-start={video.start}
                            tabIndex={0}
                            key={video.start}
                            onClick={() => {
                                showVideo(video.start);
                            }}
                            onKeyDown={(event) => {
                                if (
                                    event.code === "Space" ||
                                    event.code === "Enter"
                                ) {
                                    event.preventDefault();
                                    showVideo(video.start);
                                }
                            }}
                        >
                            <img
                                src={
                                    getServerRenderedVideoImageSrc(
                                        video.start,
                                    ) ?? video.image
                                }
                                alt={video.alt}
                                loading="lazy"
                                decoding="async"
                            />
                            <p className="caption-medium">{video.caption}</p>
                        </div>
                    ))}
                </article>
            </div>
        </section>
    );
};
